import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";

const GAP = 6;

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  position?: "top" | "bottom" | "left" | "right";
  delay?: number;
  disabled?: boolean;
  className?: string;
  wrapperClassName?: string;
  zIndex?: number;
  usePortal?: boolean;
  wrap?: boolean;
  showArrow?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = "top",
  delay = 200,
  disabled = false,
  className,
  wrapperClassName,
  zIndex = 99999,
  usePortal = true,
  wrap = true,
  showArrow = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [portalCoords, setPortalCoords] = useState<{
    top: number;
    left: number;
    arrowLeft?: number;
    arrowTop?: number;
  } | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    if (disabled) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsVisible(true), delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };

  useLayoutEffect(() => {
    if (!isVisible || !usePortal || !triggerRef.current || !popupRef.current) {
      if (!isVisible) setPortalCoords(null);
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;
    let arrowLeft = popupRect.width / 2;
    let arrowTop = popupRect.height / 2;

    switch (position) {
      case "right":
        left = triggerRect.right + GAP;
        top = triggerRect.top + triggerRect.height / 2 - popupRect.height / 2;
        arrowLeft = 0;
        break;
      case "left":
        left = triggerRect.left - popupRect.width - GAP;
        top = triggerRect.top + triggerRect.height / 2 - popupRect.height / 2;
        arrowLeft = popupRect.width;
        break;
      case "top":
        left = triggerRect.left + triggerRect.width / 2 - popupRect.width / 2;
        top = triggerRect.top - popupRect.height - GAP;
        arrowTop = popupRect.height;
        break;
      case "bottom":
        left = triggerRect.left + triggerRect.width / 2 - popupRect.width / 2;
        top = triggerRect.bottom + GAP;
        arrowTop = 0;
        break;
    }

    // Boundary corrections
    const padding = 12;
    if (left < padding) {
      const shift = padding - left;
      left += shift;
      arrowLeft -= shift;
    } else if (left + popupRect.width > window.innerWidth - padding) {
      const shift = left + popupRect.width - (window.innerWidth - padding);
      left -= shift;
      arrowLeft += shift;
    }

    if (top < padding) {
      top = padding;
    } else if (top + popupRect.height > window.innerHeight - padding) {
      top = window.innerHeight - popupRect.height - padding;
    }

    setPortalCoords((prev) => {
      if (
        prev &&
        Math.abs(prev.top - top) < 1 &&
        Math.abs(prev.left - left) < 1
      ) {
        return prev;
      }
      return { top, left, arrowLeft, arrowTop };
    });
  }, [isVisible, usePortal, position, content]);

  // UX & Accessibility Listeners
  React.useEffect(() => {
    if (!isVisible) return;

    const handleScroll = () => hideTooltip();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") hideTooltip();
    };
    const handleInteractionOutside = (e: Event) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      hideTooltip();
    };

    // Scroll with capture:true to detect scroll on any internal div
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handleInteractionOutside);
    document.addEventListener("touchstart", handleInteractionOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handleInteractionOutside);
      document.removeEventListener("touchstart", handleInteractionOutside);
    };
  }, [isVisible]);

  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-1.5",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-1.5",
    left: "right-full top-1/2 -translate-y-1/2 mr-1.5",
    right: "left-full top-1/2 -translate-y-1/2 ml-1.5",
  };

  const arrowBorders = {
    top: "border-b border-r",
    bottom: "border-t border-l",
    left: "border-t border-r",
    right: "border-b border-l",
  };

  const arrowPositioning = {
    top: "top-full left-1/2 -translate-x-1/2 -mt-1",
    bottom: "bottom-full left-1/2 -translate-x-1/2 -mb-1",
    left: "left-full top-1/2 -translate-y-1/2 -ml-1",
    right: "right-full top-1/2 -translate-y-1/2 -mr-1",
  };

  const renderPopup = (isPortal: boolean) => (
    <div
      ref={isPortal ? popupRef : undefined}
      className={cn(
        "py-2.5 px-4 text-sm text-white bg-gray-900 rounded-lg shadow-xl",
        "pointer-events-none leading-relaxed",
        wrap
          ? "whitespace-normal max-w-[280px] sm:max-w-sm text-left w-max"
          : "whitespace-nowrap",
        !isPortal && "absolute",
        !isPortal && positionClasses[position],
        className,
      )}
      style={
        isPortal
          ? {
              position: "fixed",
              left: portalCoords ? portalCoords.left : -9999,
              top: portalCoords ? portalCoords.top : -9999,
              zIndex,
              opacity: portalCoords ? 1 : 0,
              visibility: portalCoords ? "visible" : "hidden",
            }
          : { zIndex }
      }
      role="tooltip"
    >
      {content}
      {showArrow && (
        <div
          className={cn(
            "absolute w-2 h-2 bg-gray-900 border-purple-700",
            arrowBorders[position],
            !isPortal && arrowPositioning[position],
          )}
          style={
            isPortal
              ? {
                  left: portalCoords?.arrowLeft,
                  top: portalCoords?.arrowTop,
                  transform: "translate(-50%, -50%) rotate(45deg)",
                }
              : {
                  transform:
                    position === "top" || position === "bottom"
                      ? "translateX(-50%) rotate(45deg)"
                      : "translateY(-50%) rotate(45deg)",
                }
          }
        />
      )}
    </div>
  );

  return (
    <div
      ref={triggerRef}
      className={cn("relative inline-flex", wrapperClassName)}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      {isVisible &&
        !disabled &&
        content &&
        (usePortal
          ? typeof document !== "undefined" &&
            createPortal(renderPopup(true), document.body)
          : renderPopup(false))}
    </div>
  );
};

export default Tooltip;
